import { useState, useEffect } from 'react'
import styles from './ActiveConnectorCard.module.css'
import ConnectorConfigModal from './ConnectorConfigModal'
import Icon from './Icon'
import { toggleConnectorAction } from '../api/connectors'

/**
 * ActiveConnectorCard — displays the status and capabilities of an active
 * data-source connector (e.g. Zendesk, ServiceNow).
 *
 * Props:
 *   icon          {string}    — path to a .png in /public/images (e.g. "/images/zendesk.png")
 *   iconBg        {string}    — background colour behind the avatar image (e.g. "#1a4a3a")
 *   name          {string}    — connector display name (e.g. "Zendesk")
 *   description   {string}    — subtitle / URL (e.g. "acme-corp.zendesk.com")
 *   lastSynced    {string}    — human-readable sync time (e.g. "Synced 2 min ago")
 *   status        {string}    — "active" | "error"
 *   actions       {Array}     — [{ action_id: string, label: string, enabled: boolean, outcome: string|null, errorMessage: string }]
 *   connectorId   {string}    — ID passed to /api/connector/props/{connector_id} in the config modal
 *   barColor      {string}    — left-accent bar colour (hex, rgb, or CSS var name starting with "--")
 *   onConfigure   {function}  — optional override called when Configure is clicked
 *   onStatusClick {function}  — optional override called when an error status pill is clicked
 */
export default function ActiveConnectorCard({
  connectorId,
  icon,
  iconBg,
  name,
  description,
  lastSynced,
  status = 'active',
  actions = [],
  barColor,
  onConfigure,
  onStatusClick,
}) {
  const [modalOpen, setModalOpen] = useState(false)
  const [modalInitialAction, setModalInitialAction] = useState(null)
  const [activeActionError, setActiveActionError] = useState(null) // label of the action whose error is shown
  const [enabledOverrides, setEnabledOverrides] = useState({})     // action_id -> boolean (optimistic toggles)
  const [contextMenu, setContextMenu] = useState(null)             // { x, y, action } | null

  const isError = status?.toLowerCase() === 'error'

  // Resolve barColor to a valid CSS value
  const resolvedBarColor = barColor
    ? barColor.startsWith('--') ? `var(${barColor})` : barColor
    : 'var(--color-semantic-green)'

  function handleConfigureClick() {
    if (onConfigure) {
      onConfigure()
    } else {
      setModalInitialAction(null)
      setModalOpen(true)
    }
  }

  function handleConfigureAction(action) {
    setContextMenu(null)
    setModalInitialAction(action.label)
    setModalOpen(true)
  }

  function handleStatusClick() {
    if (!isError) return
    if (onStatusClick) {
      onStatusClick()
    } else {
      setModalOpen(true)
    }
  }

  // Returns the effective enabled state, preferring any optimistic override
  function getEffectiveEnabled(action) {
    return action.action_id != null && action.action_id in enabledOverrides
      ? enabledOverrides[action.action_id]
      : action.enabled
  }

  function handleActionContextMenu(e, action) {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, action })
  }

  async function handleToggleAction(action) {
    setContextMenu(null)
    const prevEnabled = getEffectiveEnabled(action)
    // Optimistic update
    setEnabledOverrides(prev => ({ ...prev, [action.action_id]: !prevEnabled }))
    try {
      await toggleConnectorAction(action.action_id)
    } catch {
      // Revert on failure
      setEnabledOverrides(prev => ({ ...prev, [action.action_id]: prevEnabled }))
    }
  }

  // Dismiss context menu on outside click or Escape
  useEffect(() => {
    if (!contextMenu) return
    function onKey(e) { if (e.key === 'Escape') setContextMenu(null) }
    function onClick() { setContextMenu(null) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('click', onClick)
    }
  }, [contextMenu])

  return (
    <>
      <div className={styles.card}>
        {/* Configurable left accent bar */}
        <div className={styles.bar} style={{ backgroundColor: resolvedBarColor }} />

        <div className={styles.content}>

          {/* ── Avatar column ── */}
          <div
            className={styles.avatar}
            style={iconBg ? { backgroundColor: iconBg } : undefined}
          >
            {icon ? (
              <img
                src={icon}
                alt={name}
                className={styles.avatarImg}
                onError={e => {
                  e.currentTarget.style.display = 'none'
                  e.currentTarget.parentElement.dataset.fallback = name.slice(0, 2).toUpperCase()
                }}
              />
            ) : (
              <span>{name.slice(0, 2).toUpperCase()}</span>
            )}
          </div>

          {/* ── Right column: all rows ── */}
          <div className={styles.rightCol}>

            {/* Title row */}
            <div className={styles.titleGroup}>
              <span className={styles.name}>{name}</span>

              <button
                className={`${styles.statusPill} ${isError ? styles.statusError : styles.statusActive}`}
                onClick={handleStatusClick}
                disabled={!isError}
                aria-label={isError ? `${name} has an error – click for details` : `${name} is active`}
              >
                {isError ? (
                  <>
                    <Icon name="info" className={styles.pillIcon} />
                    Error
                  </>
                ) : (
                  <>
                    <span className={styles.activeDot} />
                    Active
                  </>
                )}
              </button>

              <button
                className={styles.configureBtn}
                onClick={handleConfigureClick}
                aria-label={`Configure ${name}`}
              >
                <Icon name="refresh" className={styles.pillIcon} />
                Configure
              </button>
            </div>

            {/* ── Meta row: description + last synced ── */}
            <div className={styles.metaRow}>
            <span className={styles.description}>{description}</span>
            <span className={styles.lastSynced}>{lastSynced}</span>
          </div>

            {/* ── Actions row ── */}
            {actions.length > 0 && (
              <div className={styles.actionsRow}>
              {actions.map((action) => {
                const isFailed = action.outcome === 'failed'
                const isShowingError = activeActionError === action.label
                const effectiveEnabled = getEffectiveEnabled(action)
                return (
                  <span
                    key={action.label}
                    className={styles.actionWrapper}
                    onContextMenu={e => handleActionContextMenu(e, action)}
                  >
                    <button
                      className={`${styles.actionPill} ${
                        isFailed
                          ? styles.actionFailed
                          : effectiveEnabled
                            ? styles.actionEnabled
                            : styles.actionDisabled
                      }`}
                      onClick={isFailed ? () => setActiveActionError(isShowingError ? null : action.label) : undefined}
                      disabled={!isFailed}
                      aria-expanded={isFailed ? isShowingError : undefined}
                    >
                      <span className={styles.actionDot} />
                      {action.label}
                      {isFailed && (
                        <Icon name="info" className={styles.actionFailedIcon} />
                      )}
                    </button>
                    {isFailed && isShowingError && action.errorMessage && (
                      <div className={styles.actionErrorPopover}>
                        {action.errorMessage}
                      </div>
                    )}
                  </span>
                )
              })}
            </div>
            )}

          </div>{/* end rightCol */}
        </div>
      </div>

      <ConnectorConfigModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        connectorId={connectorId}
        name={name}
        status={status}
        initialAction={modalInitialAction}
      />

      {contextMenu && (
        <div
          className={styles.contextMenu}
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={e => e.stopPropagation()}
        >
          <button
            className={styles.contextMenuItem}
            onClick={() => handleToggleAction(contextMenu.action)}
          >
            {getEffectiveEnabled(contextMenu.action) ? 'Disable' : 'Enable'}
          </button>
          <button
            className={styles.contextMenuItem}
            onClick={() => handleConfigureAction(contextMenu.action)}
          >
            Configure
          </button>
        </div>
      )}
    </>
  )
}
